import React, { useEffect, useState } from 'react';
import { withFormik } from 'formik';
import { Alert, Row, Col, Form, Card, CardBody, CardFooter } from 'reactstrap';
import { Button, Field, FormAlerts, Table } from '@cordata/buffet';
import { orderBy, find } from 'lodash';
import { Link, withRouter } from 'react-router-dom';
import * as Yup from 'yup';

import {
  adminInstance as axiosAdmin,
  toastSuccess,
} from '~/utils';

import { authorizedRbInstance as rb } from '~/utils/api/instances';

import useSlice from '~/utils/slice/hook';

const frequencyOptions = [
  'Daily',
  'Weekly',
  'Monthly',
].map(r => ({
  label: r,
  value: r,
}));

const dayOfTheWeekOptions = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
].map(r => ({
  label: r,
  value: r,
}));

const formikEnhancer = withFormik({
  displayName: 'ScheduledReportFormikEnhancer',
  validateOnChange: false,
  validateOnBlur: false,

  mapPropsToValues: props => {
    const hasExistingData = props.data && props.data.id;
    const attributes = props.data?.attributes || {};

    // Remove send-emails from attributes to prevent conflicts
    const { sendEmails, ...otherAttributes } = attributes;

    return {
      dateOfTheMonth: undefined,
      dayOfTheWeek: undefined,
      send_emails: hasExistingData ? sendEmails : true,
      scheduledHour: 12, // Default to 12 PM UTC
      ...props.data,
      ...otherAttributes,
    };
  },

  validationSchema: Yup.object().shape({
    className: Yup.string()
      .required(),
    frequency: Yup.mixed()
      .required(),
    dayOfTheWeek: Yup.mixed().when('frequency', {
      is: v => v?.value === 'Weekly',
      then: Yup.mixed().required(),
    }),
    dateOfTheMonth: Yup.number().when('frequency', {
      is: v => v?.value === 'Monthly',
      then: s => s.min(1).max(31).required(),
    }),
    scheduledHour: Yup.number()
      .min(0)
      .max(23)
      .required(),
    scheduledReportLabel: Yup.string()
      .required(),
    scheduledReportName: Yup.string()
      .required(),
    userClassName: Yup.mixed()
      .required(),
    orgUid: Yup.mixed()
      .required(),
  }),

  handleSubmit: (values, { setErrors, setSubmitting, ...bag }) => {
    const { props: { history } } = bag;

    const method = values.id ? 'put' : 'post';
    const url = values.id ? `/scheduled-report/${values.id}` : '/scheduled_report';

    const p = {
      ...values,
      day: values.frequency.value === 'Monthly' ? values.dateOfTheMonth - 1 : values.frequency.value === 'Weekly' ? dayOfTheWeekOptions.indexOf(values.dayOfTheWeek) : undefined,
      frequency: values.frequency.value,
      'org-uid': values.orgUid.id,
      divisionUid: values.divisionUid ? values.divisionUid.id : undefined,
      'user-class-name': values.userClassName.value,
      'send-emails': !!values.send_emails,
      'scheduled-hour': parseInt(values.scheduledHour, 10),
    };

    axiosAdmin[method](url, p)
      .then(() => toastSuccess())
      .then(() => history.push('/organization/scheduled-reports'))
      .catch(e => setErrors({ server: e }))
      .then(() => setSubmitting(false));
  },
});

const ScheduledReportForm = ({
  values,
  errors,
  handleSubmit,
  isSubmitting,
  submitCount,
  setFieldValue,
}) => {
  const { fetch } = useSlice();

  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [availableDivisions, setAvailableDivisions] = useState([]);

  const [availableUsers, setAvailableUsers] = useState([]);
  const [reportUsers, setReportUsers] = useState([]);

  const userClassNameOptions = [{
    value: 'ReportUserOptOutService',
    label: 'Opt Out',
  }, {
    value: 'ReportUserOptInService',
    label: 'Opt In',
  }];

  const reportUsersTableColumns = [{
    id: 'displayName',
    Header: 'Display Name',
    accessor: (item) => {
      const user = availableUsers.find(u => item.attributes.userUid === u.id);
      return user ? user.attributes.displayName : '';
    },
  }, {
    id: 'delete',
    Header: 'Delete',
    accessor: () => 'Click here to delete',
    filterable: false,
  }];

  useEffect(() => {
    if (values.frequency) {
      if (values.frequency.includes('-')) {
        // backwards compat
        const [freq, dayOfTheWeek] = values.frequency.split('-');

        setFieldValue('frequency', find(frequencyOptions, ['value', freq]));
        setFieldValue('dayOfTheWeek', find(dayOfTheWeekOptions, ['value', dayOfTheWeek]));
      } else {
        setFieldValue('frequency', find(frequencyOptions, ['value', values.frequency]));
      }
    }

    if (values.day !== undefined && values.frequency) {
      if (values.frequency === 'Weekly') {
        setFieldValue('dayOfTheWeek', dayOfTheWeekOptions[values.day]);
      }

      if (values.frequency === 'Monthly') {
        setFieldValue('dateOfTheMonth', values.day + 1);
      }
    }

    if (values.userClassName) {
      setFieldValue('userClassName', find(userClassNameOptions, ['value', values.userClassName]));
    }

    // Handle send-emails field mapping
    if (values['send-emails'] !== undefined) {
      setFieldValue('send_emails', values['send-emails']);
    }

    // Handle scheduled-hour field mapping
    if (values['scheduled-hour'] !== undefined) {
      setFieldValue('scheduledHour', values['scheduled-hour']);
    } else if (values.scheduledHour === undefined) {
      setFieldValue('scheduledHour', 12); // Default to 12 PM UTC
    }

    fetch('organization')
      .then(r => r.data.data)
      .then((r) => {
        setAvailableOrganizations(r);

        if (values.orgUid) {
          setFieldValue('orgUid', find(r, ['id', values.orgUid]));
        }
      });
  }, []);

  useEffect(() => {
    if (!values.frequency || values.frequency.value === 'Daily') {
      setFieldValue('dayOfTheWeek', undefined);
      setFieldValue('dateOfTheMonth', undefined);
    } else if (values.frequency.value === 'Monthly') {
      setFieldValue('dayOfTheWeek', undefined);
    }
  }, [values.frequency]);

  useEffect(() => {
    if (values.orgUid) {
      axiosAdmin
        .get(`/app-user?org-uid=${values.orgUid.id || values.orgUid}&all-data=1&is-in-selections=true&is-client-managed=true`)
        .then(r => r.data.data)
        .then(r => orderBy(r, 'attributes.lastName', 'attributes.firstName'))
        .then(r => setAvailableUsers(r));

      fetch('division', {
        'org-uid': values.orgUid.id || values.orgUid,
      })
        .then(r => r.data.data)
        .then(setAvailableDivisions);
    }
  }, [values.orgUid]);

  useEffect(() => {
    if (typeof values.divisionUid === 'string') {
      fetch('division', {
        id: values.divisionUid,
      })
        .then(r => r.data.data)
        .then((r) => {
          setFieldValue('divisionUid', r);
        });
    }
  }, [values.divisionUid]);

  const refreshReportUserList = () => {
    if (values.relationships) {
      axiosAdmin
        .get(values.relationships.scheduledReportUsers.related)
        .then(r => r.data.data)
        .then(setReportUsers);
    }
  };

  useEffect(() => {
    if (values.userClassName) {
      refreshReportUserList();
    }
  }, [values.userClassName]);

  const addUserToScheduledReportUser = () => {
    axiosAdmin
      .post('/scheduled-report-user', {
        orgUid: values.orgUid.id,
        divisionUid: values.divisionUid ? values.divisionUid.id : undefined,
        scheduledReportUid: values.id,
        userUid: values.selectedUser.id,
      })
      .then(() => setFieldValue('selectedUser', null))
      .then(refreshReportUserList);
  };

  const deleteUserFromScheduledReportUser = (item) => {
    axiosAdmin
      .delete(`/scheduled-report-user/${item.id}`)
      .then(refreshReportUserList);
  };

  const handleTestReport = () => {
    if (values.id) {
      rb.get(`/srtest/scheduled-report/${values.id}`)
        .then(() => toastSuccess('Test report executed successfully'))
        .catch(e => console.error('Test report failed:', e));
    }
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Card>
        <CardBody>
          { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

          {values.id && (
            <Alert color="info">
              org_uid and division_uid cannot be changed on tables outside of
              the config schema at this time. In order to move this item to
              another org or division, you must delete and re-create it with
              the new org and division.
            </Alert>
          )}

          <Row form>
            <Col sm>
              <Field
                name="orgUid"
                label="Organization"
                type="select"
                options={availableOrganizations}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.attributes.orgName}
                required
                disabled={values.id}
              />
            </Col>

            <Col>
              <Field
                name="divisionUid"
                label="Division"
                type="select"
                options={availableDivisions}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.attributes.divisionName}
                disabled={values.id || !values.orgUid}
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="scheduledReportName"
                label="Report Name"
                required
              />
            </Col>

            <Col sm>
              <Field
                name="scheduledReportLabel"
                label="Report Label"
                required
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="scheduledReportDesc"
                label="Report Description"
              />
            </Col>

            <Col sm>
              <Field
                name="frequency"
                type="select"
                options={frequencyOptions}
                required
              />
            </Col>

            {values.frequency?.value === 'Weekly' && (
              <Col sm>
                <Field
                  name="dayOfTheWeek"
                  type="select"
                  options={dayOfTheWeekOptions}
                  required
                />
              </Col>
            )}

            {values.frequency?.value === 'Monthly' && (
              <Col sm>
                <Field
                  name="dateOfTheMonth"
                  type="number"
                  min={1}
                  max={31}
                  required
                  text="if number is higher than last day, then scheduler will run it on the last day. EX: 31 on February"
                />
              </Col>
            )}
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="scheduledHour"
                label="Scheduled Hour (UTC)"
                type="number"
                min={0}
                max={23}
                required
                text="Hour of day (0-23) in UTC time. Default is 12 (12 PM UTC)."
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="className"
                label="Report Class Name"
                required
              />
            </Col>

            <Col sm>
              <Field
                name="userClassName"
                label="User Selection"
                type="select"
                options={userClassNameOptions}
                required
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="send_emails"
                label="Send Emails"
                type="checkbox"
              />
            </Col>
          </Row>

          {values.userClassName && ['ReportUserOptInService', 'ReportUserOptOutService'].includes(values.userClassName.value) && (
            <>
              {!values.id && (
                <Row form>
                  <Col sm>
                    To add users to the list of users for this report,
                    please save this first and come back and edit it.
                  </Col>
                </Row>
              )}

              {values.id && (
                <>
                  <hr />

                  <Row form>
                    <Col sm>
                      <Field
                        name="selectedUser"
                        label={`Add User To ${find(userClassNameOptions, ['value', values.userClassName.value]).label} List`}
                        type="select"
                        options={availableUsers.filter(au =>
                          !reportUsers.map(ru => ru.attributes.userUid).includes(au.id))}
                        noOptionsMessage={() => 'You must select an organization first.'}
                        getOptionValue={o => o.id}
                        getOptionLabel={o => o.attributes.displayName}
                      />
                    </Col>
                  </Row>

                  <Row form>
                    <Col sm>
                      <Button
                        type="button"
                        disabled={!values.selectedUser}
                        onClick={addUserToScheduledReportUser}
                        block
                      >
                        Add User To List
                      </Button>
                    </Col>
                  </Row>

                  <hr />

                  <div>
                    <h3>
                      {values.userClassName.value === 'ReportUserOptInService' ? 'Opted-In Users' : 'Opted-Out Users'}
                    </h3>
                  </div>

                  <Row form>
                    <Col sm>
                      <Table
                        data={reportUsers}
                        columns={reportUsersTableColumns}
                        defaultSorted={[{ id: 'userUid' }]}
                        filterable
                        defaultFilterMethod={(filter, row) =>
                          row[filter.id].toLowerCase().includes(filter.value.toLowerCase())
                        }
                        getTdProps={(state, rowInfo, column) => ({
                          onClick: () => {
                            if (column.id === 'delete') {
                              deleteUserFromScheduledReportUser(rowInfo.original);
                            }
                          },
                        })}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button
              type="cancel"
              enabled={isSubmitting}
              tag={Link}
              to="/organization/scheduled-reports"
            >
              Cancel
            </Button>

            <Button
              type="button"
              disabled={!values.id}
              onClick={handleTestReport}
            >
              Test Report
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Working ...' : 'Save'}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </Form>
  );
};

export default withRouter(formikEnhancer(ScheduledReportForm));
